import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Mail, Send, Search, ChevronDown, RefreshCw, Maximize2, Users, Briefcase, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECIPIENT_TYPES = [
  { id: 'apprenants', label: 'Apprenants', icon: GraduationCap },
  { id: 'intervenants', label: 'Intervenants', icon: Briefcase },
  { id: 'clients', label: 'Clients', icon: Users },
];

const EMAIL_TABS = [
  { id: 'sent', label: 'E-mails envoyés' },
  { id: 'scheduled', label: 'E-mails planifiés' },
  { id: 'automatic', label: 'E-mails automatiques' },
];

const SuiviEmails = ({ sessionId }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipientType, setRecipientType] = useState('apprenants');
  const [emailTab, setEmailTab] = useState('sent');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchEmails = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/emails-history?recipient_type=${recipientType}`, { credentials: 'include' });
      if (res.ok) setEmails(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [sessionId, recipientType]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const handleResend = async (emailId) => {
    toast.success('E-mail renvoyé avec succès');
  };

  const filtered = emails.filter(em =>
    em.recipient_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    em.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 mt-4" data-testid="suivi-emails-content">
      {/* Recipient type selector */}
      <div className="flex gap-3" data-testid="email-recipient-types">
        {RECIPIENT_TYPES.map(rt => {
          const Icon = rt.icon;
          return (
            <button key={rt.id}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-medium transition-all ${
                recipientType === rt.id
                  ? 'bg-violet-50 dark:bg-violet-900/30 border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 hover:border-violet-200'
              }`}
              onClick={() => setRecipientType(rt.id)}
              data-testid={`email-type-${rt.id}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                recipientType === rt.id ? 'bg-violet-100 dark:bg-violet-800' : 'bg-slate-100 dark:bg-slate-700'
              }`}>
                <Icon className={`h-4 w-4 ${recipientType === rt.id ? 'text-violet-600' : 'text-slate-400'}`} />
              </div>
              {rt.label}
            </button>
          );
        })}
      </div>

      {/* Heading */}
      <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
        <Send className="h-5 w-5 text-violet-500" />
        Envoyer un e-mail
      </h3>

      {/* Email sub-tabs */}
      <div className="flex gap-0 border-b border-slate-200 dark:border-slate-700" data-testid="email-sub-tabs">
        {EMAIL_TABS.map(t => (
          <button key={t.id}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              emailTab === t.id
                ? 'border-violet-600 text-violet-700 dark:text-violet-400 bg-violet-50/50 dark:bg-violet-900/10'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setEmailTab(t.id)}
            data-testid={`email-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Email table */}
      {emailTab === 'sent' && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden" data-testid="emails-table">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-0 bg-slate-50 dark:bg-slate-800/60 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
            <div className="col-span-2 px-4 py-3 flex items-center gap-1">Envoyé le <ChevronDown className="h-3 w-3" /></div>
            <div className="col-span-2 px-4 py-3 flex items-center gap-1">Email Destinataire <Search className="h-3 w-3" /></div>
            <div className="col-span-2 px-4 py-3 flex items-center gap-1">Type <Search className="h-3 w-3" /></div>
            <div className="col-span-3 px-4 py-3 flex items-center gap-1">Sujet <Search className="h-3 w-3" /></div>
            <div className="col-span-2 px-4 py-3 flex items-center gap-1">Statut <Search className="h-3 w-3" /></div>
            <div className="col-span-1 px-4 py-3">Actions</div>
          </div>

          {loading ? (
            <div className="px-6 py-8 text-center text-sm text-slate-400">Chargement...</div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-slate-400" data-testid="no-emails">
              <Mail className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p>Aucun e-mail envoyé pour cette catégorie.</p>
            </div>
          ) : filtered.map(em => (
            <div key={em.email_id} className="grid grid-cols-12 gap-0 border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
              data-testid={`email-row-${em.email_id}`}>
              <div className="col-span-2 px-4 py-3 flex items-center">
                <span className="text-xs text-slate-600 dark:text-slate-300">
                  {em.sent_at ? new Date(em.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </span>
              </div>
              <div className="col-span-2 px-4 py-3 flex items-center">
                <span className="text-xs text-slate-700 dark:text-slate-300 truncate">{em.recipient_email}</span>
              </div>
              <div className="col-span-2 px-4 py-3 flex items-center">
                <span className="text-xs text-slate-500">{em.email_type}</span>
              </div>
              <div className="col-span-3 px-4 py-3 flex items-center">
                <span className="text-xs text-slate-700 dark:text-slate-300 truncate">{em.subject}</span>
              </div>
              <div className="col-span-2 px-4 py-3 flex items-center">
                {em.status === 'reçu et ouvert' ? (
                  <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">Reçu et ouvert</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-200">Envoyé</Badge>
                )}
              </div>
              <div className="col-span-1 px-4 py-3 flex items-center">
                <button onClick={() => handleResend(em.email_id)}
                  className="text-xs text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1"
                  data-testid={`resend-email-${em.email_id}`}>
                  <RefreshCw className="h-3 w-3" />Renvoyer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {emailTab === 'scheduled' && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center" data-testid="scheduled-emails-placeholder">
          <Mail className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Aucun e-mail planifié.</p>
          <p className="text-xs text-slate-400 mt-1">Les e-mails planifiés seront affichés ici.</p>
        </div>
      )}

      {emailTab === 'automatic' && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center" data-testid="automatic-emails-placeholder">
          <Mail className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Aucun e-mail automatique configuré.</p>
          <p className="text-xs text-slate-400 mt-1">Configurez des e-mails automatiques pour les rappels et notifications.</p>
        </div>
      )}
    </div>
  );
};

export default SuiviEmails;
